import { CardItem, ContentItem } from 'types/content-types'

// ============================================
// Landing Page Variant Content Types & Data
// ============================================

export interface HeroButton {
	text: string
	href: string
	style: 'primary' | 'outlined'
	openInNewTab?: boolean
}

export interface LandingVariantContent {
	meta: {
		title: string
		description: string
		keywords: string
	}
	hero: {
		eyebrow?: string
		headline: [string, string]
		highlightPhrase: string
		descriptionLead: string
		earlyAccessIntro?: string
		earlyAccessBullets?: string[]
		buttons: HeroButton[]
		backgroundImage: string
	}
	features: {
		titleLine1: string
		titleLine2: string
		description: string
		cards: CardItem[]
	}
	about: {
		title: string
		body: string
		image: string
	}
	dashboard: {
		title: string
		body: string
		image: string
		checklist: string[]
	}
	freeTrial: {
		title: string
		buttonText: string
	}
}

export const landingContentFull: LandingVariantContent = {
	meta: {
		title: 'Autonomous Software Testing Agent',
		description:
			'ASTA performs fully autonomous smoke, accessibility, performance, security, and content-quality testing of every build so you can deploy with confidence.',
		keywords:
			'ASTA, Testing, Autonomous, Software Testing Agent, Accessibility, Security, CI/CD',
	},
	hero: {
		headline: ['Automate Your Tests.', 'Cover Your Apps.'],
		highlightPhrase:
			'smoke, accessibility, performance, security, and content-quality testing',
		descriptionLead: 'ASTA instantly performs fully autonomous',
		earlyAccessIntro:
			"Get early access to ASTA.\nIf it's a fit, we'll offer you:",
		earlyAccessBullets: [
			'Free Plus subscription for life',
			'Concierge setup and support services',
			'Free product tuning for your app',
		],
		buttons: [
			{
				text: 'Get a demo',
				href: 'https://outlook.office365.com/owa/calendar/ASTADemoScheduling@bpsconsulting.com/bookings/',
				style: 'outlined',
				openInNewTab: true,
			},
			{
				text: 'Get early access',
				href: 'https://forms.office.com/r/rm048fwmxS',
				style: 'primary',
				openInNewTab: true,
			},
		],
		backgroundImage: 'assets/images/landing/bg-mockup-default.png',
	},
	features: {
		titleLine1: 'Introducing ASTA:',
		titleLine2: 'The Autonomous Software Testing Agent',
		description:
			'ASTA is the first tool to perform fully autonomous testing of enterprise web applications. No scripting required! ASTA explores and tests your web applications using configurable rules to verify functionality, accessibility, and performance. It eliminates the need for manual scripting for many test cases and enables testers to shift their efforts to more value-added activities.',
		cards: [
			{
				title: 'Features',
				content: [
					'Explores and tests any web app out-of-the-box',
					'Verifies pages, links, buttons, and forms',
					'Supports configurable business rules',
					'Security testing for vulnerabilities',
					'Grammar & spellcheck with custom dictionaries',
					'Readability analysis',
					'Verifies accessibility (WCAG and Section 508)',
					'Performs end-to-end performance testing',
					'CI/CD integration via GitHub Actions',
					'AI-assisted flows & summaries',
				],
				icon: 'assets/images/landing/img-feature1.png',
			},
			{
				title: 'Benefits',
				content: [
					'Faster: up to 100x faster than manual testing',
					'Cheaper: eliminates the need to develop many automation scripts',
					'Quicker: enables new functions, modules, or entire applications to be tested immediately',
					'Better: provides objective test coverage statistics and frees testers to focus on high-value tests',
				],
				icon: 'assets/images/landing/img-feature2.png',
			},
			{
				title: 'Uses',
				content: [
					'Basic functional testing',
					'Smoke testing',
					'Regression testing',
					'Accessibility testing',
					'Performance testing',
					'Security testing',
					'Content quality testing',
				],
				icon: 'assets/images/landing/img-feature3.png',
			},
		],
	},
	about: {
		title: "So Smart It's Simple",
		body: 'From the Start page, enter the base URL of your application and click "Start". That\'s it! ASTA explores your application, testing as it goes. The agent logs all tests and results for reporting and problem analysis. The agent also builds a model of the application and uses it to optimize testing and report test coverage.',
		image: 'assets/images/landing/asta-start-page.png',
	},
	dashboard: {
		title: 'Actionable Results',
		body: 'The dashboard report provides a graphical summary of test results. You can drill down to see details for reported errors.',
		image: 'assets/images/landing/asta-dashboard.png',
		checklist: [
			'See overall test coverage and failure rate',
			'See what pages need attention most',
			'Zoom in on functionality or accessibility issues',
		],
	},
	freeTrial: {
		title: 'Sign-up for a free trial!',
		buttonText: 'Sign-up Now',
	},
}

export const landingContentQa: LandingVariantContent = {
	meta: {
		title: 'Web App Testing & Website Quality Assessment',
		description:
			'ASTA audits your entire website for accessibility, usability, readability, spelling, link integrity, and performance to ensure compliance and delight visitors.',
		keywords:
			'ASTA, Website Audit, Accessibility Testing, WCAG, Content Quality, Link Checker, Performance',
	},
	hero: {
		eyebrow: 'Web App Testing | Website Quality Assessment',
		headline: ['Audit Your Website.', 'Deliver Flawless Experiences.'],
		highlightPhrase:
			'accessibility, usability, readability, spelling, link integrity, and page performance',
		descriptionLead: 'ASTA scans your entire website to verify',
		earlyAccessIntro:
			"Get early access to ASTA.\nIf it's a fit, we'll offer you:",
		earlyAccessBullets: [
			'Free Plus subscription for life',
			'Concierge setup and support services',
			'Free product tuning for your site',
		],
		buttons: [
			{
				text: 'Get a demo',
				href: 'https://outlook.office365.com/owa/calendar/ASTADemoScheduling@bpsconsulting.com/bookings/',
				style: 'outlined',
				openInNewTab: true,
			},
			{
				text: 'Get early access',
				href: 'https://forms.office.com/r/rm048fwmxS',
				style: 'primary',
				openInNewTab: true,
			},
		],
		backgroundImage: 'assets/images/landing/bg-mockup-default.png',
	},
	features: {
		titleLine1: 'Comprehensive Website Auditing',
		titleLine2: 'Powered by Intelligent Automation',
		description:
			'ASTA automatically crawls and audits your entire website, checking every page for accessibility compliance, content quality, broken links, and performance issues. Get actionable reports that help you fix problems before your visitors find them.',
		cards: [
			{
				title: 'What We Check',
				content: [
					'Accessibility compliance (WCAG 2.1 & Section 508)',
					'Spelling and grammar errors',
					'Readability scores and analysis',
					'Broken links (internal and external)',
					'Page load performance',
					'Missing alt text and meta descriptions',
					'Form usability issues',
				],
				icon: 'assets/images/landing/img-feature1.png',
			},
			{
				title: 'Benefits',
				content: [
					'Compliance confidence: meet WCAG and Section 508 requirements',
					'Consistent brand voice: catch spelling and grammar issues',
					'Better SEO: no broken links or missing metadata',
					'Faster load times: identify performance bottlenecks',
					'Improved UX: fix usability issues site-wide',
				],
				icon: 'assets/images/landing/img-feature2.png',
			},
			{
				title: 'Use Cases',
				content: [
					'Pre-launch website audits',
					'Ongoing quality monitoring',
					'Accessibility compliance verification',
					'Content quality gates',
					'SEO health checks',
					'Regression testing after updates',
				],
				icon: 'assets/images/landing/img-feature3.png',
			},
		],
	},
	about: {
		title: 'Comprehensive Website Auditing',
		body: 'Enter your website URL and click "Start". ASTA crawls every page, checking for accessibility issues, broken links, spelling errors, and performance problems. Get a complete picture of your website\'s quality in minutes, not days.',
		image: 'assets/images/landing/asta-start-page.png',
	},
	dashboard: {
		title: 'Actionable Audit Reports',
		body: 'The dashboard provides a clear summary of all issues found across your website. Prioritize fixes by impact and drill down to see exactly where problems occur.',
		image: 'assets/images/landing/asta-dashboard.png',
		checklist: [
			'See accessibility compliance scores',
			'Identify pages with the most issues',
			'Track content quality across your site',
		],
	},
	freeTrial: {
		title: 'Start Your Free Website Audit!',
		buttonText: 'Audit My Site',
	},
}

export const IntroCardData: CardItem[] = [
	{
		title: 'Features',
		content: [
			'Explores and tests any web app out-of-the-box',
			'Verifies pages, links, buttons, and forms',
			'Supports configurable business rules',
			'Reads and enforces form specifications',
			'Verifies accessibility (WCAG and Section 508)',
			'Performs end-to-end performance testing',
			'Runs from any browser',
		],
		icon: 'assets/images/landing/img-feature1.png',
	},
	{
		title: 'Benefits',
		content: [
			'Faster: up to 100x faster than manual testing',
			'Cheaper: eliminates the need to develop many automation scripts',
			'Quicker: enables new functions, modules, or entire applications to be tested immediately',
			'Better: provides objective test coverage statistics and frees testers to focus on high-value tests',
		],
		icon: 'assets/images/landing/img-feature2.png',
	},
	{
		title: 'Uses',
		content: [
			'Basic functional testing',
			'Smoke testing',
			'Regression testing',
			'Accessibility testing',
			'Performance testing',
		],
		icon: 'assets/images/landing/img-feature3.png',
	},
]

export const IntroContentData: ContentItem = {
	title: '',
	content:
		'ASTA is the first tool to perform fully autonomous testing of enterprise web applications. No scripting required! ASTA explores and tests your web applications using configurable rules to verify functionality, accessibility, and performance. It eliminates the need for manual scripting for many test cases and enables testers to shift their efforts to more value-added activities.',
}

export const EnterpriseCardData: CardItem[] = [
	{
		title: 'Efficient Coverage',
		content: [
			'ASTA builds a model of your application and uses it to optimize testing. The agent uses the model to remember what has already been tested and to seek out untested pages and components. The result is better test coverage in less time. You also get a meaningful measure of test coverage in terms of the percentage of application functionality tested. Test every part of every build and deploy with confidence.',
		],
	},
	{
		title: 'Comprehensive Testing',
		content: [
			'ASTA uses generalized rules to check for correct behavior across your entire website. The agent will identify and verify the behavior no matter where it is. The agent also generates both valid and invalid data to ensure every form handles both correctly. No more drudgery of creating and maintaining hundreds of test scripts to check basic functionality...the agent does the work for you.',
		],
	},
	{
		title: "You're In Control",
		content: [
			'Need to test a particular scenario? ASTA allows you to direct the test agent using custom flows, rules, and data. Want to focus testing on a particular component? Drop it on the work queue and let the agent take care of it. Mix and match autonomous testing and directed testing however you want.',
		],
	},
]

export const EnterpriseContentData: ContentItem = {
	title: 'Enterprise-Grade Testing Features',
	content:
		'ASTA was built to test large, complex, enterprise web applications. It has been used successfully on web applications with hundreds of forms and millions of users.',
}

export const StepCardData: CardItem[] = [
	{
		title: 'Enter the application URL',
		content: [
			'Enter the base URL of your application and click "Start". The agent will crawl and test your application until it is done.',
		],
		icon: 'assets/images/landing/one.png',
	},
	{
		title: 'Watch it run (or not!)',
		content: [
			'The "Status" page provides an overview of the testing in progress, including the number of components found and the percentage tested. It also displays a screenshot of the page currently being tested.',
		],
		icon: 'assets/images/landing/two.png',
	},
	{
		title: 'View results',
		content: [
			'Select the "Model" view to show the test results for every page and component of your application. Review the summary statistics, and drill down for error details.',
		],
		icon: 'assets/images/landing/three.png',
	},
]

export const StepContentData: ContentItem = {
	title: "It's as Easy as 1-2-3...",
	content:
		'Get started easily, and up your testing capability immediately. No need to learn a new tool or scripting language.',
}

export const SupportData: CardItem[] = [
	{
		title: 'Technical Documentation',
		content: ['Read the ASTA user and technical guides.'],
		icon: 'assets/images/landing/img-feature1.svg',
		href: '/docs',
	},
	{
		title: 'Request Support',
		content: ['Submit a support request via email.'],
		icon: 'assets/images/landing/img-feature2.svg',
		href: 'mailto:support@bpsconsulting.com?subject=ASTA%20support%20request',
	},
	{
		title: 'Ask the Community',
		content: [
			'COMING SOON - Ask the ASTA user community for help or information.',
		],
		icon: 'assets/images/landing/img-feature3.svg',
	},
]

export const PricingContentData: ContentItem = {
	title: 'ASTA Pricing',
	content:
		'ASTA pricing is designed to be simple and transparent. The Plus and Pro versions are priced per-application. The Enterprise version includes tailoring ASTA to your application and environment, live training, and phone support. We offer a full customer satisfaction guarantee: we will provide a full refund if ASTA does not deliver the value that you expected.',
}
